/**
 * jc-cortex.js - the one place where this theme reads Cortex.
 *
 * Delivery promise, Christmas deadline, shipping costs, the free shipping
 * threshold and the free hat offer all come from one public Cortex document:
 * /apps/cortex/public/verzendbelofte/<winkel_id>, first-party through the
 * shop's app proxy. Nothing of it is fixed in the theme or in the theme
 * settings (decision Thomas, memory project_verzendbelofte_een_bron). Cortex
 * is managed at /instellingen/verzendopties.
 *
 * Why this theme has its own reader next to the shared standard-delivery.js:
 * japchristmas.eu sells in Dutch, German and English, and Cortex writes its
 * promise in Dutch only. This file takes Cortex's own text on Dutch pages and
 * composes the same sentence from Cortex's dates and times on the other two,
 * so every language makes the same promise. It also covers the shipping cost,
 * the threshold bar and the gift offer, which the shared block does not.
 *
 * The rule for every element: it renders hidden and only appears once Cortex
 * has a value for it. No answer, no value or a currency other than EUR means
 * the element stays hidden. A missing promise is better than a wrong one.
 *
 * Placeholders, filled wherever they appear, also after the cart drawer
 * re-renders (MutationObserver):
 *   [data-cortex="delivery"]          delivery promise, or the Christmas line
 *                                     inside the Christmas window; never the
 *                                     Christmas line where the element says
 *                                     data-christmas="false" (the announcement
 *                                     bar, Thomas 10-10: "Ik wil niks in de balk")
 *   [data-cortex="shipping-threshold"] "Gratis verzending vanaf EUR 20,00"
 *   [data-cortex="shipping-threshold-short"] "Vanaf EUR 20,00", under a
 *                                     heading that names free shipping
 *   [data-cortex="checkout-shipping"] under the checkout button of the cart,
 *                                     what shipping adds to this cart
 *                                     (data-cart-total, cents): "+ EUR 6,00
 *                                     verzending · gratis vanaf EUR 60,00",
 *                                     "Gratis verzending", or with a paid
 *                                     express class next to free standard
 *                                     shipping "Gratis verzending · express
 *                                     EUR 4,95" (snippets/cart-shipping-line.liquid);
 *                                     it holds its place unseen like the
 *                                     progress bar
 *   [data-cortex="gift-offer"]        shown only while the free hat offer runs;
 *                                     [data-cortex-gift-count] gets the number
 *                                     of sweaters the first tier takes
 *   [data-cortex="gift-discount"]     the discount title on a gift line in
 *                                     the cart, on /en and /de: Cortex's
 *                                     Dutch tier name (data-title) in the
 *                                     visitor's language
 *   [data-cortex-gift-progress]       the one progress bar of the cart, with
 *                                     free shipping and every gift tier as its
 *                                     milestones (snippets/cart-gift-progress.liquid);
 *                                     the one exception to "renders hidden": it
 *                                     holds its place unseen (data-waiting) until
 *                                     Cortex answers, so the cart lines below
 *                                     do not jump down
 *   [data-cortex-slide]               announcement slide, shown once its
 *                                     placeholder has text
 *
 * The contract with Cortex (agreed with session jc-cortex-instellingen,
 * 04-10-2026; Cortex only ever extends it):
 *   regios[].landcodes / klassen[]   the class for the visitor's country,
 *                                    'standaard' first
 *   klasse.per_land[COUNTRY]         levering and kerstdeadline per country;
 *                                    read before the class's own values
 *   klasse.prijs_incl_btw,
 *   klasse.gratis_vanaf_incl_btw     what the checkout charges; a threshold
 *                                    of 0 means always free
 *   klasse 'express'                 next to free standard shipping, its
 *                                    price is named in the cart
 *   cadeau_aanbod[]                  one entry per gift tier, only while that
 *                                    tier runs in Cortex AND its Shopify
 *                                    discount is active: voorwaarde.min_aantal
 *                                    sweaters give cadeau.aantal items from
 *                                    cadeau.handles
 *   voorstel                         true only on ?voorstel=true, a preview of
 *                                    a proposal: never shown
 *
 * Gift tiers (Thomas 09-10-2026, LICHT-BESLUITEN L30): 2 sweaters = a hat of
 * your choice, 3 or more = a hat and a lint remover. Every tier is its own
 * Shopify discount and the discounts do not stack, so a tier lists everything
 * it gives (the 3+ tier names the hats AND the lint remover, 2 items).
 * giftPlan() turns a tier into what the visitor can be offered: the hats in
 * it are ONE choice (the hats of the theme's hat collection), every other
 * product is a fixed gift. A tier whose fixed gift is not on sale, or that has
 * no hat on sale to choose, is skipped for the tier below: the theme never
 * offers what the checkout would not give.
 *
 * Other scripts wait for the data with window.JapCortex.ready, which resolves
 * to { payload, timeZone, option, land, shipping, express, gift, gifts } or
 * null. `shipping` is { price, freeFrom } in cents for the standard class,
 * `express` { price } for a paid express class of the same region, or null.
 * window.JapCortex.shortMoney writes whole euros without cents ("€6").
 * `timeZone` is Cortex's (payload.tijdzone), the one every date and time of
 * the promise is counted in. `gifts` holds the tiers, lowest first; `gift` is
 * the first tier in the older shape ({ code, minSweaters, productIds }).
 * window.JapCortex.christmasMoment(state, start) says where the visitor stands
 * against the Christmas deadline for their country (the Christmas row under
 * the buy button follows it).
 */
(() => {
  if (window.JapCortex) return;

  // A placeholder that holds its place for Cortex's answer (the cart's progress
  // bar) keeps its room while <html data-jc-cortex> is there (assets/jc-aov.css):
  // "waiting" from snippets/jc-cortex-config.liquid before the page paints,
  // "ready" once Cortex answered. No answer, or none within
  // ANSWER_TIMEOUT_MS, removes it, and every such place closes, also in a
  // drawer rendered later.
  const html = document.documentElement;
  const STATE_ATTRIBUTE = 'data-jc-cortex';
  // As long as the reservation clock waits for its answer (ASK_TIMEOUT_MS in
  // assets/cart-countdown.js). A later answer still fills everything.
  const ANSWER_TIMEOUT_MS = 8000;

  const configElement = document.getElementById('jc-cortex-config');
  if (!configElement) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    html.removeAttribute(STATE_ATTRIBUTE);
    return;
  }

  const CACHE_MS = 10 * 60 * 1000;
  const PREFERRED_CLASS = 'standaard';
  // Until Cortex says otherwise (payload.tijdzone, the time zone of the
  // warehouse the routes start from).
  const DEFAULT_TIME_ZONE = 'Europe/Amsterdam';
  // From this many days before the Christmas deadline the row under the buy
  // button says "Voor kerst" instead of "Ruim op tijd voor kerst".
  const CHRISTMAS_LAST_WEEK_DAYS = 7;
  const strings = config.strings || {};

  // ------------------------------------------------------------------ loading

  function readCache(key) {
    try {
      const entry = JSON.parse(sessionStorage.getItem(key) || 'null');
      return entry && Date.now() - entry.at < CACHE_MS ? entry.data : null;
    } catch (error) {
      return null;
    }
  }

  function writeCache(key, data) {
    try {
      sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), data }));
    } catch (error) {
      // Storage full or blocked: the next page asks Cortex again.
    }
  }

  async function loadPayload() {
    const key = `jc-cortex:${config.shopId}`;
    const cached = readCache(key);
    if (cached) return cached;
    const response = await fetch(`/apps/cortex/public/verzendbelofte/${encodeURIComponent(config.shopId)}`, {
      headers: { Accept: 'application/json' },
      credentials: 'omit'
    });
    if (!response.ok) return null;
    const data = await response.json();
    writeCache(key, data);
    return data;
  }

  // ------------------------------------------------------------- reading data

  /** Euro amount as a string ("2.95") to cents, or null when absent. */
  function toCents(value) {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? Math.round(number * 100) : null;
  }

  /** The classes Cortex offers for the visitor's country. */
  function optionsFor(payload, country) {
    const region = (payload?.regios || []).find((item) =>
      String(item.landcodes || '')
        .split(/[\s,;]+/)
        .map((code) => code.trim().toUpperCase())
        .includes(country)
    );
    return region?.klassen || [];
  }

  /** The class the promise is about: 'standaard' first. */
  function optionFor(options) {
    return options.find((item) => item.klasse === PREFERRED_CLASS) || options[0] || null;
  }

  /** Cortex's values for one country inside the class, when it has them. */
  function landFor(option, country) {
    return option?.per_land?.[country] || null;
  }

  /**
   * Price and threshold for this visitor, in cents. Only for EUR carts:
   * Cortex amounts are euros, and an amount in the wrong currency is worse
   * than none. A class that says it does not match the checkout is skipped.
   */
  function shippingFor(option) {
    if (!option || config.currency !== 'EUR') return null;
    if (option.gelijk_aan_checkout === false) return null;
    const freeFrom = toCents(option.gratis_vanaf_incl_btw);
    // Free from 0 is always free, whatever the price says.
    const price = freeFrom === 0 ? 0 : toCents(option.prijs_incl_btw);
    if (price === null && freeFrom === null) return null;
    return { price, freeFrom };
  }

  /** A paid express class next to the standard one, in cents; null when there is none. */
  function expressFor(options, standard) {
    const express = options.find((item) => item.klasse === 'express' && item !== standard);
    const shipping = shippingFor(express);
    return shipping && shipping.price > 0 ? { price: shipping.price } : null;
  }

  /** True while an offer's start and end (ISO times) put it in force right now. */
  function inForce(item) {
    const now = Date.now();
    const start = Date.parse(item.start_op || '');
    const end = Date.parse(item.eind_op || '');
    return !(Number.isFinite(start) && start > now) && !(Number.isFinite(end) && end <= now);
  }

  /**
   * The gift tiers, lowest first, only while Cortex lists them in
   * cadeau_aanbod. Cortex fills that list only when a tier is switched on in
   * Cortex AND the Shopify discount behind it (Buy X get Y on the gifts) is
   * active, so the theme never promises a gift the checkout does not give.
   */
  async function giftsFor(payload) {
    const offers = (payload?.cadeau_aanbod || [])
      .filter((item) =>
        Number(item?.voorwaarde?.min_aantal) > 0
        && (item?.cadeau?.handles || []).length > 0
        && inForce(item)
      )
      .sort((a, b) => Number(a.voorwaarde.min_aantal) - Number(b.voorwaarde.min_aantal));
    if (offers.length === 0) return null;

    const products = await giftProducts([...new Set(offers.flatMap((offer) => offer.cadeau.handles))]);
    const tiers = offers
      .map((offer) => ({
        code: offer.code,
        name: offer.naam || '',
        minSweaters: Number(offer.voorwaarde.min_aantal),
        count: Math.max(1, Number(offer.cadeau.aantal) || 1),
        products: offer.cadeau.handles.map((handle) => products[handle]).filter(Boolean)
      }))
      .filter((tier) => tier.products.length > 0);
    return tiers.length ? tiers : null;
  }

  /**
   * Cortex names the gifts by their Dutch handle. Handles are translated per
   * language, so every gift is read once through the storefront's own
   * /products/<handle>.js on the root locale: its id, the variant that goes in
   * the cart, whether it is on sale, its picture, and its price in the
   * visitor's currency (the market decides it, not the path). Cached per
   * currency.
   */
  async function giftProducts(handles) {
    const key = `jc-cortex-gifts:${config.currency}:${config.country}:${handles.join(',')}`;
    const cached = readCache(key);
    if (cached) return cached;
    const found = {};
    await Promise.all(handles.map(async (handle) => {
      try {
        const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const product = await response.json();
        const variant = (product.variants || []).find((item) => item.available) || (product.variants || [])[0];
        if (!product.id || !variant) return;
        const image = product.featured_image ? String(product.featured_image) : '';
        found[handle] = {
          handle,
          id: String(product.id),
          variantId: variant.id,
          price: Number(variant.price) || 0,
          available: Boolean(variant.available) && (product.variants || []).length === 1,
          image: image ? `${image.startsWith('//') ? 'https:' : ''}${image}${image.includes('?') ? '&' : '?'}width=160` : null,
          title: product.title || ''
        };
      } catch (error) {
        // An unreadable gift is left out; giftPlan then skips its tier.
      }
    }));
    writeCache(key, found);
    return found;
  }

  /**
   * What the visitor can be offered for `sweaters` sweaters: the highest tier
   * they reach that the shop can give right now. `hats` are the hats of the
   * theme's hat collection ({ productId, inStock, ... }); the hats of a tier are
   * one choice, every other product in it is a fixed gift. A tier is skipped
   * when a fixed gift is not on sale, when it asks for more than one hat (the
   * block offers one choice), or when none of its hats is in stock.
   * Returns { tier, hats, extras, hatSlots } or null.
   */
  function giftPlan(tiers, sweaters, hats) {
    if (!tiers || !(sweaters > 0)) return null;
    const hatIds = new Set((hats || []).map((hat) => String(hat.productId)));
    const reached = tiers.filter((tier) => tier.minSweaters <= sweaters).sort((a, b) => b.minSweaters - a.minSweaters);
    for (const tier of reached) {
      const inTier = new Set(tier.products.map((product) => product.id));
      const extras = tier.products.filter((product) => !hatIds.has(product.id));
      const hatSlots = tier.count - extras.length;
      if (extras.some((product) => !product.available) || hatSlots < 0 || hatSlots > 1) continue;
      const offered = hatSlots ? (hats || []).filter((hat) => hat.inStock && inTier.has(String(hat.productId))) : [];
      if (hatSlots && offered.length === 0) continue;
      return { tier, hats: offered, extras, hatSlots };
    }
    return null;
  }

  /** The short name of a fixed gift: the theme's own word for it, else its title. */
  function giftName(product) {
    return (config.giftNames || {})[product.id] || product.title;
  }

  /**
   * The cart lists the Shopify discount behind a gift on the free line and,
   * at 0, on the sweaters that earned it. Its title is Cortex's Dutch tier
   * name (cadeau_aanbod[].naam) and Shopify has no translation for discount
   * titles. On /en and /de snippets/cart-items.liquid leaves those lines empty
   * and this writes the same tier in the visitor's language: the hat, every
   * fixed gift by its short name (config.giftNames) and the tier's sweater
   * count. A title that is no Cortex tier stays hidden.
   */
  function giftDiscountText(title, gifts) {
    const tier = (gifts || []).find((item) => item.name === title);
    if (!tier) return null;
    const names = config.giftNames || {};
    const extras = tier.products.filter((product) => names[product.id]).map((product) => names[product.id]);
    const values = { count: String(tier.minSweaters), name: extras.join(', ') };
    return fill(extras.length ? strings.giftDiscountExtra : strings.giftDiscount, values);
  }

  // ---------------------------------------------------------------- formatting

  /** Fills the [name] placeholders that snippets/jc-cortex-config.liquid left in. */
  function fill(template, values) {
    return String(template || '').replace(/\[(\w+)\]/g, (match, name) =>
      values[name] !== undefined ? values[name] : match
    );
  }

  /** Cents to money in the shop's own format, e.g. "€20,00". */
  function formatMoney(cents) {
    const format = config.moneyFormat || '€{{amount_with_comma_separator}}';
    const value = (cents / 100).toFixed(2);
    const [whole, decimals] = value.split('.');
    const grouped = (separator) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, name) => {
      switch (name) {
        case 'amount':
          return `${grouped(',')}.${decimals}`;
        case 'amount_no_decimals':
          return grouped(',');
        case 'amount_with_comma_separator':
          return `${grouped('.')},${decimals}`;
        case 'amount_no_decimals_with_comma_separator':
          return grouped('.');
        default:
          return `${grouped('.')},${decimals}`;
      }
    });
  }

  /** Whole euros without cents, for a short line: "€6" for 600, "€4,95" for 495. */
  function shortMoney(cents) {
    const money = formatMoney(cents);
    return cents % 100 === 0 ? money.replace(/[.,]00(?!\d)/, '') : money;
  }

  /** Today and now in a time zone, as sortable strings. */
  function nowParts(timeZone) {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(new Date());
    const get = (type) => parts.find((part) => part.type === type)?.value || '';
    return { date: `${get('year')}-${get('month')}-${get('day')}`, time: `${get('hour')}:${get('minute')}` };
  }

  function daysBetween(fromIso, toIso) {
    return Math.round((Date.parse(`${toIso}T12:00:00Z`) - Date.parse(`${fromIso}T12:00:00Z`)) / 86400000);
  }

  /** "morgen", "dinsdag", or "dinsdag 6 oktober" further ahead. */
  function dayLabel(isoDate, timeZone) {
    const diff = daysBetween(nowParts(timeZone).date, isoDate);
    if (diff === 1) return strings.tomorrow;
    const options = diff >= 0 && diff <= 6
      ? { weekday: 'long', timeZone: 'UTC' }
      : { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' };
    return new Intl.DateTimeFormat(config.locale, options).format(new Date(`${isoDate}T12:00:00Z`));
  }

  /** A date (YYYY-MM-DD) in the visitor's language, "dinsdag 22 december" by default. */
  function dateLabel(isoDate, options = { weekday: 'long', day: 'numeric', month: 'long' }) {
    return new Intl.DateTimeFormat(config.locale, { ...options, timeZone: 'UTC' }).format(new Date(`${isoDate}T12:00:00Z`));
  }

  // ------------------------------------------------------------------- texts

  /**
   * The delivery promise that applies right now. Past the cut-off Cortex's
   * na_besteltijd applies. Cortex's own text wins in the language it wrote it
   * in; otherwise the sentence is composed from its dates and cut-off time.
   */
  function deliveryText({ option, land, payload, timeZone }) {
    const levering = land?.levering || option?.levering;
    if (!levering?.volledig) return null;

    const seconds = Number(levering.seconden_tot_besteltijd);
    const calculated = Date.parse(payload?.berekend_op || '');
    const pastCutoff = Number.isFinite(seconds) && Number.isFinite(calculated)
      && Date.now() >= calculated + seconds * 1000;
    const promise = pastCutoff ? levering.na_besteltijd : levering;
    if (!promise) return null;

    const ownText = promise.teksten?.[config.language] || (config.language === 'nl' ? promise.tekst : null);
    if (ownText) return ownText;

    if (!promise.leverdatum_min) return null;
    const from = dayLabel(promise.leverdatum_min, timeZone);
    const day = promise.leverdatum_max && promise.leverdatum_max !== promise.leverdatum_min
      ? fill(strings.deliveryRange, { from, to: dayLabel(promise.leverdatum_max, timeZone) })
      : from;
    if (promise.voor_besteltijd && option.besteltijd) {
      return fill(strings.deliveryBeforeCutoff, { time: option.besteltijd, day });
    }
    return fill(strings.deliveryNow, { day });
  }

  /**
   * Where the visitor stands right now against Cortex's Christmas deadline
   * for their country (date and order time, kerstdeadline_datum/_tijd),
   * counted in the time zone of Cortex's route. Null when the theme does not
   * talk about Christmas: switched off, before `start`, after the window's
   * end, or no complete deadline in Cortex for this country (then nothing is
   * shown; a deadline is never filled in here).
   *   phase 'early'     more than CHRISTMAS_LAST_WEEK_DAYS days to go
   *         'days'      1 to CHRISTMAS_LAST_WEEK_DAYS days to go
   *         'last-day'  the deadline is today
   *         'too-late'  the deadline has passed
   * with the deadline's `date` and `time`. Both sides are wall-clock times in
   * the same zone, so the comparison needs no offsets.
   */
  function christmasMoment(state, start = config.christmas?.start) {
    const { option, land, timeZone } = state || {};
    const date = land?.kerstdeadline_datum || option?.kerstdeadline_datum;
    const time = land?.kerstdeadline_tijd || option?.kerstdeadline_tijd;
    if (!date || !time || !config.christmas?.enabled) return null;
    const now = nowParts(timeZone);
    if ((start && now.date < start) || now.date > config.christmas.end) return null;

    if (`${now.date} ${now.time}` >= `${date} ${time}`) return { phase: 'too-late', date, time };
    const days = daysBetween(now.date, date);
    if (days === 0) return { phase: 'last-day', date, time };
    return { phase: days <= CHRISTMAS_LAST_WEEK_DAYS ? 'days' : 'early', date, time };
  }

  /**
   * The delivery promise inside the Christmas window: the last order moment
   * that still arrives before Christmas, or, once that has passed, the honest
   * message that it will not. The window only decides WHEN; the deadline
   * itself comes from Cortex.
   */
  function christmasText(state) {
    const moment = christmasMoment(state);
    if (!moment) return null;
    if (moment.phase === 'too-late') return strings.christmasTooLate;
    return fill(strings.christmasDeadline, { date: dateLabel(moment.date), time: moment.time });
  }

  function thresholdText(shipping) {
    if (!shipping) return null;
    if (shipping.price === 0) return strings.shippingAlwaysFree;
    if (shipping.freeFrom > 0) return fill(strings.shippingFreeFrom, { amount: formatMoney(shipping.freeFrom) });
    if (shipping.price > 0) return fill(strings.shippingCost, { amount: formatMoney(shipping.price) });
    return null;
  }

  /**
   * Under the checkout button: what shipping adds to a cart of `total` cents.
   * Free when the standard class costs nothing or the cart reaches its
   * threshold; then a paid express class is named too.
   */
  function cartShippingText(shipping, express, total) {
    if (!shipping || !Number.isFinite(total)) return null;
    const free = shipping.price === 0 || (shipping.freeFrom > 0 && total >= shipping.freeFrom);
    if (free) {
      return express ? fill(strings.cartShippingFreeExpress, { price: formatMoney(express.price) }) : strings.cartShippingFree;
    }
    if (!(shipping.price > 0)) return null;
    if (shipping.freeFrom > 0) {
      return fill(strings.cartShippingLine, { price: formatMoney(shipping.price), amount: formatMoney(shipping.freeFrom) });
    }
    return fill(strings.cartShippingCost, { price: formatMoney(shipping.price) });
  }

  /** Under a heading that already says "Gratis verzending": just "Vanaf EUR 20,00". */
  function shortThresholdText(shipping) {
    if (!shipping) return null;
    if (shipping.price === 0) return strings.shippingAlwaysFree;
    if (shipping.freeFrom > 0) return fill(strings.shippingFromShort, { amount: formatMoney(shipping.freeFrom) });
    return null;
  }

  // ------------------------------------------------------------------ the DOM

  /** An announcement slide appears once something in it has a value. */
  function revealSlide(element) {
    const slide = element.closest('[data-cortex-slide]');
    if (slide) slide.hidden = false;
  }

  function show(element, text) {
    const target = element.querySelector('[data-cortex-text]') || element;
    target.textContent = text;
    element.hidden = false;
    revealSlide(element);
  }

  /**
   * The free shipping milestone: how far the cart total is from Cortex's
   * threshold. None when Cortex has no threshold, when shipping is always
   * free, or when the cart total is unknown.
   */
  function shippingMilestone(element, shipping) {
    const total = Number(element.dataset.cartTotal);
    if (!shipping || !(shipping.freeFrom > 0) || shipping.price === 0 || !Number.isFinite(total)) return null;
    const reached = total >= shipping.freeFrom;
    return {
      key: 'shipping',
      progress: Math.min(1, total / shipping.freeFrom),
      reached,
      text: reached
        ? strings.freeShippingReached
        : fill(strings.freeShippingRemaining, { amount: formatMoney(shipping.freeFrom - total) })
    };
  }

  const listOf = (value) => String(value || '').split(',').filter(Boolean);
  const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

  /**
   * The gift milestones: one per tier the shop can give at its own sweater
   * count (giftPlan), so a tier whose fixed gift is sold out has no milestone.
   * Each is labelled with what it adds to the tier below: the hat, or the
   * fixed gift by its short name.
   */
  function giftMilestones(element, gifts, hats) {
    if (!gifts) return [];
    const sweaters = Number(element.dataset.sweaters) || 0;
    const milestones = [];
    let below = null;
    for (const tier of gifts) {
      const plan = giftPlan(gifts, tier.minSweaters, hats);
      if (!plan || plan.tier !== tier) continue;
      const known = new Set(below ? below.extras.map((product) => product.id) : []);
      const added = plan.extras.find((product) => !known.has(product.id));
      const remaining = Math.max(0, tier.minSweaters - sweaters);
      const one = remaining === 1;
      milestones.push({
        key: `gift-${milestones.length}`,
        gift: true,
        label: added ? capitalize(fill(strings.giftExtraLabel, { name: giftName(added) })) : strings.giftHatLabel,
        progress: Math.min(1, sweaters / tier.minSweaters),
        reached: remaining === 0,
        minSweaters: tier.minSweaters,
        text: added
          ? fill(one ? strings.giftExtraRemainingOne : strings.giftExtraRemainingOther, { count: remaining, name: giftName(added) })
          : fill(one ? strings.giftRemainingOne : strings.giftRemainingOther, { count: remaining })
      });
      below = plan;
    }
    return milestones;
  }

  /** One marker per milestone: the shipping one and the gift template from the snippet, the rest cloned. */
  function milestoneMarker(element, milestone) {
    if (!milestone.gift) return element.querySelector('[data-milestone="shipping"]');
    let marker = element.querySelector(`[data-milestone="${milestone.key}"]`);
    if (!marker) {
      const template = element.querySelector('[data-milestone="gift-0"]');
      if (!template) return null;
      marker = template.cloneNode(true);
      marker.dataset.milestone = milestone.key;
      template.parentNode.appendChild(marker);
    }
    const label = marker.querySelector('.cart-progress__label');
    if (label) label.textContent = milestone.label;
    return marker;
  }

  /**
   * The fixed gifts the cart still needs, as add buttons next to the hat
   * choices (the same tile and button as a hat: [data-upsell-add] in
   * assets/cart-set-upsell.js adds it at its normal price; the discount makes
   * it free).
   */
  function renderExtraChoices(choices, extras) {
    choices.querySelectorAll('[data-gift-extra]').forEach((tile) => tile.remove());
    const template = choices.querySelector('[data-gift-extra-template]');
    extras.forEach((product) => {
      if (!template) return;
      const tile = template.content.firstElementChild.cloneNode(true);
      tile.setAttribute('data-gift-extra', '');
      tile.dataset.productId = product.id;
      const media = tile.querySelector('[data-gift-extra-media]');
      if (media && product.image) {
        const image = document.createElement('img');
        image.src = product.image;
        image.alt = '';
        image.loading = 'lazy';
        image.className = 'img-fit';
        media.appendChild(image);
      }
      const title = tile.querySelector('[data-gift-extra-title]');
      if (title) title.textContent = giftName(product);
      const button = tile.querySelector('[data-upsell-add]');
      if (button) {
        button.dataset.variantId = String(product.variantId);
        button.dataset.handle = product.handle;
        button.textContent = strings.giftAdd;
        button.setAttribute('aria-label', fill(strings.giftExtraAdd, { name: giftName(product) }));
      }
      choices.appendChild(tile);
    });
  }

  /**
   * The one progress bar of the cart: free shipping and every gift tier as
   * milestones on one bar (Sweenly). The milestone closest to being reached
   * sits first; the bar fills segment by segment. The line above the bar asks
   * for a gift that is earned and not yet in the cart (pick the hat, add the
   * fixed gift), otherwise names what the next milestone still needs. Without
   * a milestone the bar stays hidden.
   */
  function renderCartProgress(element, shipping, gifts) {
    const hats = listOf(element.dataset.hatIds).map((productId) => ({
      productId,
      inStock: listOf(element.dataset.hatsInStock).includes(productId)
    }));
    const milestones = [
      element.dataset.shippingEnabled === 'true' ? shippingMilestone(element, shipping) : null,
      ...(element.dataset.giftEnabled === 'true' ? giftMilestones(element, gifts, hats) : [])
    ]
      .filter(Boolean)
      .sort((a, b) => b.progress - a.progress);
    // The place it held while Cortex was asked is now filled or given up.
    element.removeAttribute('data-waiting');
    if (milestones.length === 0) {
      element.hidden = true;
      return;
    }

    // Read by assets/cart-set-upsell.css: one grid column per milestone.
    element.style.setProperty('--milestones', String(milestones.length));
    const segment = 100 / milestones.length;
    let filled = 0;
    for (const milestone of milestones) {
      filled += segment * milestone.progress;
      if (!milestone.reached) break;
    }
    const bar = element.querySelector('[data-cortex-bar]');
    if (bar) bar.style.setProperty('--progress', `${filled}%`);

    const shown = new Set();
    milestones.forEach((milestone, index) => {
      const marker = milestoneMarker(element, milestone);
      if (!marker) return;
      shown.add(marker);
      marker.hidden = false;
      marker.style.setProperty('--column', String(index + 1));
      marker.classList.toggle('is-reached', milestone.reached);
      marker.classList.toggle('is-last', index === milestones.length - 1);
    });
    element.querySelectorAll('[data-milestone]').forEach((marker) => {
      if (!shown.has(marker)) marker.hidden = true;
    });

    // What the cart holds now, against what the reached tier gives.
    const sweaters = Number(element.dataset.sweaters) || 0;
    const plan = element.dataset.giftEnabled === 'true' ? giftPlan(gifts, sweaters, hats) : null;
    const inCart = listOf(element.dataset.cartProductIds);
    const variantsInCart = listOf(element.dataset.cartVariantIds);
    const hatMissing = Boolean(plan && plan.hatSlots && !plan.hats.some((hat) => inCart.includes(String(hat.productId))));
    const extrasMissing = plan ? plan.extras.filter((product) => !variantsInCart.includes(String(product.variantId))) : [];

    const choices = element.querySelector('[data-cortex-gift-choices]');
    if (choices) {
      let visible = 0;
      choices.querySelectorAll('[data-product-id]:not([data-gift-extra])').forEach((choice) => {
        const offered = hatMissing && plan.hats.some((hat) => String(hat.productId) === String(choice.dataset.productId));
        choice.hidden = !offered;
        if (offered) visible += 1;
      });
      // The fixed gift waits for the hat: the 3+ discount needs both in the
      // cart, so the hat comes first and the fixed gift right after.
      renderExtraChoices(choices, hatMissing ? [] : extrasMissing);
      visible += hatMissing ? 0 : extrasMissing.length;
      choices.hidden = visible === 0;
    }

    // A return brings the gift back too (Thomas 09-10): said once a tier is earned.
    const returnLine = element.querySelector('[data-gift-return]');
    if (returnLine) returnLine.hidden = !plan;

    const next = milestones.find((milestone) => !milestone.reached);
    let text;
    if (hatMissing) text = strings.giftChoose;
    else if (extrasMissing.length) text = fill(strings.giftExtraAdd, { name: giftName(extrasMissing[0]) });
    else if (next) text = next.text;
    else if (plan) text = plan.extras.length ? strings.giftClaimedAll : strings.giftClaimed;
    else text = strings.freeShippingReached;

    // Read by assets/cart-set-upsell.js: the CartGiftProgress event (first
    // tier), and the paid hat or gift that steps aside once it is free.
    const first = milestones.filter((milestone) => milestone.gift).sort((a, b) => a.minSweaters - b.minSweaters)[0];
    if (first) {
      element.dataset.giftReached = String(first.reached);
      element.dataset.thresholdType = 'sweaters';
      element.dataset.threshold = String(first.minSweaters);
      element.dataset.progress = String(sweaters);
      element.dataset.giftProductIds = plan
        ? [...plan.hats.map((hat) => String(hat.productId)), ...plan.extras.map((product) => product.id)].join(',')
        : '';
    }
    show(element, text);
  }

  /** Elements that only exist while the offer runs, with the first tier's sweater count. */
  function renderGiftOffer(element, gifts) {
    if (!gifts) return;
    element.querySelectorAll('[data-cortex-gift-count]').forEach((count) => {
      count.textContent = String(gifts[0].minSweaters);
    });
    element.hidden = false;
    revealSlide(element);
  }

  function apply(state, root = document) {
    if (!state) return;
    const { shipping, express, gifts } = state;

    root.querySelectorAll('[data-cortex]:not([data-cortex-done])').forEach((element) => {
      element.setAttribute('data-cortex-done', '');
      let text = null;
      switch (element.dataset.cortex) {
        case 'delivery':
          text = (element.dataset.christmas !== 'false' && christmasText(state)) || deliveryText(state);
          if (text) show(element, text);
          break;
        case 'shipping-threshold':
          text = thresholdText(shipping);
          if (text) show(element, text);
          break;
        case 'shipping-threshold-short':
          text = shortThresholdText(shipping);
          if (text) show(element, text);
          break;
        case 'checkout-shipping':
          // The place it held while Cortex was asked is now filled or given up.
          element.removeAttribute('data-waiting');
          text = cartShippingText(shipping, express, Number(element.dataset.cartTotal));
          if (text) show(element, text);
          break;
        case 'gift-offer':
          renderGiftOffer(element, gifts);
          break;
        case 'gift-discount':
          text = giftDiscountText(element.dataset.title, gifts);
          if (text) show(element, text);
          break;
        default:
          break;
      }
    });

    root.querySelectorAll('[data-cortex-gift-progress]:not([data-cortex-done])').forEach((element) => {
      element.setAttribute('data-cortex-done', '');
      renderCartProgress(element, shipping, gifts);
    });

    document.dispatchEvent(new CustomEvent('jc-cortex:applied', { detail: state }));
  }

  // -------------------------------------------------------------------- start

  const ready = loadPayload()
    .then(async (payload) => {
      // A proposal preview (?voorstel=true) is never shown to a customer.
      if (!payload || payload.voorstel === true) return null;
      const country = String(config.country || 'NL').toUpperCase();
      const options = optionsFor(payload, country);
      const option = optionFor(options);
      const gifts = await giftsFor(payload);
      return {
        payload,
        timeZone: payload.tijdzone || DEFAULT_TIME_ZONE,
        option,
        land: landFor(option, country),
        shipping: shippingFor(option),
        express: expressFor(options, option),
        gifts,
        gift: gifts
          ? { code: gifts[0].code, minSweaters: gifts[0].minSweaters, productIds: gifts[0].products.map((product) => product.id) }
          : null
      };
    })
    .catch(() => null);

  window.JapCortex = { ready, formatMoney, shortMoney, giftPlan, giftName, christmasMoment, dateLabel };

  const giveUp = setTimeout(() => html.removeAttribute(STATE_ATTRIBUTE), ANSWER_TIMEOUT_MS);
  ready.then((state) => {
    clearTimeout(giveUp);
    if (!state) {
      html.removeAttribute(STATE_ATTRIBUTE);
      return;
    }
    html.setAttribute(STATE_ATTRIBUTE, 'ready');
    apply(state);

    // The cart drawer and the cart page replace their HTML on every change;
    // new placeholders arrive hidden and are filled here.
    let scheduled = false;
    new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        apply(state);
      });
    }).observe(document.body, { childList: true, subtree: true });
  });
})();
